<template>
  <component
    :is="rootTag"
    v-bind="rootProps"
    :class="cardClasses"
    @click="onRootClick"
    @keydown="onRootKeydown">
    <div
      v-if="$slots['top-right']"
      class="absolute top-sm right-sm flex items-center gap-xs">
      <slot name="top-right" />
    </div>
    <div
      v-if="$slots.icon"
      class="mb-sm h-6 w-6 text-action [&>svg]:h-full [&>svg]:w-full">
      <slot name="icon" />
    </div>
    <h3
      v-if="title"
      class="m-0 text-base leading-6 font-semibold text-text-primary">
      {{ title }}
    </h3>
    <p
      v-if="subtitle"
      class="mt-1 mb-0 text-sm text-text-secondary">
      {{ subtitle }}
    </p>
    <slot />
  </component>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import type { RouteLocationRaw } from 'vue-router'

export default {
  name: 'Card',

  emits: ['on-click'],

  props: {
    variant: {
      type: String as PropType<
        'default' | 'selected' | 'interactive' | 'inset'
      >,
      default: 'default',
    },
    title: { type: String, default: '' },
    subtitle: { type: String, default: '' },
    isFlush: { type: Boolean, default: false },
    to: {
      type: [String, Object] as PropType<RouteLocationRaw | undefined>,
      default: undefined,
    },
  },

  computed: {
    isInteractive(): boolean {
      return this.variant === 'interactive'
    },

    rootTag(): string {
      if (this.isInteractive && this.to) return 'RouterLink'
      if (this.isInteractive) return 'div'
      return 'div'
    },

    rootProps(): Record<string, unknown> {
      if (this.isInteractive && this.to) return { to: this.to }
      if (this.isInteractive) return { role: 'button', tabindex: 0 }
      return {}
    },

    cardClasses(): (string | Record<string, boolean>)[] {
      return [
        'relative',
        'rounded-lg',
        this.isFlush ? 'overflow-hidden p-0' : 'p-md',
        {
          'border border-border-subtle bg-surface': this.variant === 'default',
          'border-2 border-border-focus bg-surface':
            this.variant === 'selected',
          'border-0 bg-surface-muted': this.variant === 'inset',
          'cursor-pointer border border-border-subtle bg-surface text-inherit no-underline transition-colors duration-fast ease-standard hover:border-border-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus':
            this.isInteractive,
        },
      ]
    },
  },

  methods: {
    onRootClick(event: Event) {
      if (this.isInteractive && !this.to) this.$emit('on-click', event)
    },

    onRootKeydown(event: KeyboardEvent) {
      if (!this.isInteractive || this.to) return
      if (event.key !== 'Enter' && event.key !== ' ') return
      event.preventDefault()
      this.$emit('on-click', event)
    },
  },
}
</script>
